import "./PromptAndAnswerTab.scss";
import "./ToolbarStyles.scss";

import type { EditorState } from "@codemirror/state";
import React, {
  createRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, OverlayTrigger } from "react-bootstrap";
import { Link } from "react-router-dom";

import { ProjectIcon } from "../../../../components/icons";
import { QuestionIcon } from "../../../../components/icons/QuestionIcon";
import { TrashIcon } from "../../../../components/icons/TrashIcon";
import { ItemSwitcher } from "../../../../components/ItemSwitcher/ItemSwitcher";
import { PromptWithContext } from "../../../../components/PromptWithContext";
import { ReadMore } from "../../../../components/ReadMore";
// import { Button, Spinner } from "react-bootstrap";
// import { CustomModal } from "../../../../components/CustomModal";
import { Helpers } from "../../../../components/SlateEditor/helpers";
import { SlateEditor } from "../../../../components/SlateEditor/SlateEditor";
import { StatusBadge } from "../../../../components/StatusBadge";
import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";
import { cacheTypes } from "../../../../constants/cacheTypes";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import type {
  ICacheItemResponsePayload,
  IChoice,
  IPromptContext,
  IPrompType,
  ISimilarPrompt,
  IUpdateCacheItemRequest,
} from "../../../../hooks/reactQueryHooks/useCacheItems";
import {
  useAddAnswer,
  useDeleteAnswer,
  useUpdateCacheItem,
} from "../../../../hooks/reactQueryHooks/useCacheItems";
// import { SuggestionModal } from "../SuggestionModal/SuggestionModal";
import { MAX_CHOICES } from "../../../../hooks/reactQueryHooks/useProxy";
import type { Message } from "../../../../hooks/reactQueryHooks/useRequests";
import { usePrompt } from "../../../../hooks/usePrompt";
import { CacheHistoryItem } from "../CacheHistoryItem/CacheHistoryItem";
import { DeleteAnswerModal } from "../DeleteAnswerModal/DeleteAnswerModal";
import { AnswerJsonEditor } from "./AnswerJsonEditor";
import { SimilarPromptsComponent } from "./SimilarPromptsComponent";
import { useChoicesState } from "./useChoicesState";

export interface IProps {
  id: string;
  cacheData: ICacheItemResponsePayload | null;
  appName: string;
  similarPrompts: ISimilarPrompt[];
  projId: string;
  promptContext?: IPromptContext;
  promptType?: IPrompType;
  cacheHistory: Message[];
}

export type IPrompt = ISimilarPrompt;

export interface IJsonEditorRef {
  state: EditorState;
}

const defaultChoices: IChoice[] = [];

const PromptAndAnswerTab: React.FC<IProps> = memo(
  ({
    id,
    cacheData,
    appName,
    similarPrompts: similarPromptsProps,
    projId,
    promptType,
    promptContext,
    cacheHistory,
  }) => {
    const { currentOrganization, accessAllowed } = useOrganizationContext();
    const {
      currentChoice,
      choicesState,
      handleSlateChoiceChange,
      handleJsonChoiceChange,
      removeAnswer,
      setChoiceAsSaved,
    } = useChoicesState(cacheData?.choices || defaultChoices);

    const [isBlocking, setIsBlocking] = useState(false);

    usePrompt("You have unsaved changes. Do you want to leave?", isBlocking);

    const editor = createRef<any>();
    const JSONEditorRef = useRef<IJsonEditorRef>();
    // const [showSuggestionModal, setShowSuggestionModal] =
    //   useState<boolean>(false);
    const [similarPrompts, setSimilarPrompts] = useState<IPrompt[]>([]);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

    useEffect(() => {
      const res = choicesState.find(
        choice => choice.unsavedChanges || choice.jsonUnsavedChanges,
      );
      setIsBlocking(!!res || hasChanges);
    }, [choicesState, hasChanges]);

    const updateMutation = useUpdateCacheItem();
    const removeAnswerMutation = useDeleteAnswer();
    const addAnswerMutation = useAddAnswer();

    // const getSuggestion = () => {
    //   setShowSuggestionModal(true);
    // };

    useEffect(() => {
      setSimilarPrompts(similarPromptsProps);
    }, [similarPromptsProps]);

    // const handleSuggestionModalCancel = () => {
    //   setShowSuggestionModal(false);
    // };
    // const useSuggestion = () => {
    //   setShowSuggestionModal(false);
    // };

    const saveContent = (value: string) => {
      const choice = choicesState[currentChoice - 1];
      if (!choice) return;
      if (!choice.id) {
        addAnswerMutation.mutate({
          organizationId: currentOrganization?.id || "",
          applicationId: projId,
          cacheItemId: id,
          answer: value,
        });
        return;
      }
      const cacheItem: IUpdateCacheItemRequest = {
        organizationId: currentOrganization?.id || "",
        cacheItemId: id,
        cacheItem: {
          answer: value,
        },
        applicationId: projId,
        choiceId: choicesState[currentChoice - 1].id,
      };
      updateMutation.mutate(cacheItem);
    };

    useEffect(() => {
      if (updateMutation.isSuccess) {
        const savedChoice = updateMutation.data;
        setChoiceAsSaved(savedChoice);
        updateMutation.reset();
        setHasChanges(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateMutation.isSuccess]);

    useEffect(() => {
      if (addAnswerMutation.isSuccess) {
        const savedChoice = addAnswerMutation.data;
        setChoiceAsSaved(savedChoice);
        addAnswerMutation.reset();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addAnswerMutation.isSuccess]);

    const defaultValue = useMemo(() => {
      if (!choicesState[currentChoice - 1])
        return [{ type: "paragraph", children: [{ text: "" }] }];
      const item = choicesState[currentChoice - 1];
      if (item.unsavedChanges) {
        return item.unsavedChanges;
      }
      if (item.isNew) {
        return [{ type: "paragraph", children: [{ text: "" }] }];
      }
      return Helpers.markdown2slate(
        choicesState[currentChoice - 1].message.content,
      );
    }, [currentChoice, choicesState]);

    const jsonDefaultValue = useMemo(() => {
      if (!choicesState[currentChoice - 1]) return "";
      const item = choicesState[currentChoice - 1];

      if (
        item.jsonUnsavedChanges !== null &&
        item.jsonUnsavedChanges !== undefined
      ) {
        return item.jsonUnsavedChanges;
      }
      return item.message.content;
    }, [currentChoice, choicesState]);

    useEffect(() => {
      if (!editor.current) return;
      if (
        choicesState.length &&
        choicesState[currentChoice - 1] &&
        choicesState[currentChoice - 1].history
      ) {
        editor.current.history = choicesState[currentChoice - 1].history;
      } else {
        editor.current.history = {
          undos: [],
          redos: [],
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChoice, choicesState]);

    const promptWithContext = useMemo(() => {
      if (promptContext?.data) {
        const { data } = promptContext;
        if (data.query && data.context) {
          return {
            question: data.query,
            context: data.context,
          };
        }
      }
      return null;
    }, [promptContext]);

    const deleteAnswer = () => {
      const currentAnswer = choicesState[currentChoice - 1];
      if (!currentAnswer.id) {
        removeAnswer();
        setIsDeleteModalOpen(false);
        return;
      }
      removeAnswerMutation.mutate({
        organizationId: currentOrganization?.id || "",
        applicationId: projId,
        cacheItemId: id,
        id: currentAnswer.id,
      });
    };

    useEffect(() => {
      if (removeAnswerMutation.isSuccess) {
        removeAnswer();
        removeAnswerMutation.reset();
        setIsDeleteModalOpen(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeAnswerMutation.isSuccess]);

    const changeChoice = (item: number) => {
      if (editor.current) {
        handleSlateChoiceChange(hasChanges, editor.current, item);
        return;
      }
      if (
        choicesState[currentChoice - 1].isJSON &&
        JSONEditorRef.current?.state
      ) {
        handleJsonChoiceChange(hasChanges, JSONEditorRef.current.state, item);
      }
    };

    const handleAddAnswer = () => {
      handleSlateChoiceChange(hasChanges, editor.current);
    };

    const handleDeleteAnswerClick = () => {
      setIsDeleteModalOpen(true);
    };

    const closeModal = useCallback(() => {
      setIsDeleteModalOpen(false);
    }, []);

    return (
      <div className="mx-0 prompt-answer">
        <h6 className="prompt-answer__title mt-0">
          <ProjectIcon size={12} /> &nbsp;Project:&nbsp;
          <Link to={`/projects/${projId}`}> {appName}</Link>
        </h6>
        <hr className="my-8" />

        <h6 className="prompt-answer__title">
          Main prompt &nbsp;
          {promptType && cacheTypes[promptType] && (
            <StatusBadge status={cacheTypes[promptType]} variant="purple" />
          )}
        </h6>
        {promptWithContext ? (
          <PromptWithContext {...promptWithContext} />
        ) : (
          <p>
            <ReadMore text={cacheData?.prompt || ""} />
          </p>
        )}

        <div>
          <hr className="my-8" />
          <SimilarPromptsComponent
            orgId={currentOrganization?.id || ""}
            cacheItemId={id}
            prompts={similarPrompts}
            applicationId={projId}
          />
        </div>
        <hr className="my-8" />

        <div className="">
          <div className="d-flex flex-sm-row flex-column  justify-content-between align-items-center">
            <h6 className="prompt-answer__title mb-0 mt-0 align-items-baseline align-self-baseline align-self-sm-center">
              Answer
              {choicesState.length > 1 && ` #${currentChoice}`}
              {hasChanges && (
                <span className="prompt-answer__subtitle">UNSAVED CHANGES</span>
              )}
            </h6>

            {/* <p
            className="prompt-answer__suggestion"
            onClick={getSuggestion}
          >
            Use suggested answer
          </p> */}
            <div className="d-flex align-self-end">
              {!!choicesState.length && choicesState.length > 1 && (
                <ItemSwitcher
                  currentItem={currentChoice}
                  itemsCount={choicesState?.length || 0}
                  onChange={changeChoice}
                  isLooped={true}
                  disabled={
                    updateMutation.isLoading ||
                    addAnswerMutation.isLoading ||
                    removeAnswerMutation.isLoading
                  }
                />
              )}
              <Button
                onClick={handleAddAnswer}
                variant="primary"
                className="ms-4"
                disabled={choicesState.length >= MAX_CHOICES}
              >
                + <span className="d-sm-inline d-none">Add answer</span>
              </Button>

              <Button
                variant="light"
                className="delete-button d-flex cursor-pointer align-items-center justify-content-center  ms-4"
                onClick={handleDeleteAnswerClick}
                disabled={choicesState.length === 1}
                data-testid="delete-answer"
              >
                <TrashIcon />
              </Button>
            </div>
          </div>
          {!!choicesState.length &&
            (choicesState[currentChoice - 1].isJSON ? (
              <AnswerJsonEditor
                answerData={choicesState[currentChoice - 1]}
                initialValue={jsonDefaultValue}
                isLoading={
                  updateMutation.isLoading || addAnswerMutation.isLoading
                }
                onSave={saveContent}
                setHasChanges={setHasChanges}
                ref={JSONEditorRef}
                withBanner={!accessAllowed}
              />
            ) : (
              <SlateEditor
                isLoading={
                  updateMutation.isLoading || addAnswerMutation.isLoading
                }
                onSave={saveContent}
                defaultValue={defaultValue}
                accessAllowed={accessAllowed}
                placeholder="Enter the answer here..."
                needUpdate={true}
                ref={editor}
                setHasChanges={setHasChanges}
                focusOnMount={choicesState[currentChoice - 1]?.isNew}
                type="text"
              />
            ))}
        </div>
        {!!cacheHistory.length && (
          <>
            <hr className="my-8" />
            <h6 className="prompt-answer__title mb-0">
              History
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip(
                  "History shows all previous prompts leading up to the main last prompt, providing context for the current interaction.",
                )}
              >
                <span className="title-question-icon">
                  <QuestionIcon />
                </span>
              </OverlayTrigger>
            </h6>
            {cacheHistory.map((item: Message, index) => (
              <CacheHistoryItem key={index} message={item} />
            ))}
          </>
        )}

        {/* <SuggestionModal
        visible={showSuggestionModal}
        onCancel={handleSuggestionModalCancel}
        suggestion={suggestion.suggestion}
        changes={suggestion.changes}
        saveSuggestion={useSuggestion}
      /> */}
        {isDeleteModalOpen && (
          <DeleteAnswerModal
            onClose={closeModal}
            onSuccess={deleteAnswer}
            isProcessing={removeAnswerMutation.isLoading}
          />
        )}
      </div>
    );
  },
);

export { PromptAndAnswerTab };
